import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, X, Copy, ChevronDown, Trash2 } from "lucide-react";

/**
 * CloneTenantModal — pull a tenant from a REMOTE deployment INTO the
 * current one. The operator is logged into this (target) super-admin
 * UI; the form collects:
 *   • Source API URL  (with a "Saved deployments" dropdown)
 *   • Source tenant slug
 *   • Source super-admin phone + OTP (we mint a token server-side)
 *   • Target slug (optional — defaults to source slug)
 *   • Toggles: --full, --wipe, include_app_config
 *   • "Save this source for next time" — adds to deployment-targets
 *
 * Hits POST /api/superadmin/clone-from which runs the entire
 * export→import server-to-server. No CORS, no browser-side big
 * payloads, source credentials never persisted.
 */
export default function CloneTenantModal({ onClose, onSuccess }) {
  const [sourceApi, setSourceApi] = useState("");
  const [sourceSlug, setSourceSlug] = useState("");
  const [sourcePhone, setSourcePhone] = useState("9858558555");
  const [sourceOtp, setSourceOtp] = useState("");
  const [targetSlug, setTargetSlug] = useState("");
  const [full, setFull] = useState(false);
  const [wipe, setWipe] = useState(false);
  const [includeAppConfig, setIncludeAppConfig] = useState(true);
  const [saveAs, setSaveAs] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedTargets, setSavedTargets] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load the operator's previously-used source URLs so they don't
  // re-type ecommerceapi.localappstore.in / preview.example.com / etc.
  useEffect(() => {
    api.get("/superadmin/deployment-targets")
      .then(({ data }) => setSavedTargets(data?.items || []))
      .catch(() => {});
  }, []);

  const removeSavedTarget = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/superadmin/deployment-targets/${id}`);
      setSavedTargets((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      toast.error("Couldn't delete saved deployment");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!sourceApi.startsWith("http")) {
      toast.error("Source API must start with http(s)://");
      return;
    }
    if (!sourceSlug || !sourceOtp) {
      toast.error("Source slug and OTP are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/clone-from", {
        source_api: sourceApi.replace(/\/$/, ""),
        source_slug: sourceSlug.trim(),
        source_phone: sourcePhone.trim(),
        source_otp: sourceOtp.trim(),
        target_slug: targetSlug.trim() || null,
        full,
        wipe,
        include_app_config: includeAppConfig,
        save_as: saveAs.trim() || null,
      });
      const counts = data?.inserted_counts || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(
        `Cloned ${data?.source?.slug} → ${data?.target?.slug} — ${total} docs across ${Object.keys(counts).length} collections`,
      );
      onSuccess?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clone failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      data-testid="clone-tenant-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-[hsl(var(--kb-border))] w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 p-5 border-b border-[hsl(var(--kb-border))]">
          <div>
            <div className="overline">Pull from a remote deployment</div>
            <h2 className="font-display text-2xl flex items-center gap-2"><Copy size={18} /> Clone tenant</h2>
            <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">
              We log into the source as super-admin, export its data, and import it here. Source credentials are never stored.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy}
                  className="p-1 hover:bg-[hsl(var(--kb-surface-alt))]"
                  data-testid="clone-modal-close" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Source API ----------------------------------------------- */}
          <div>
            <label className="overline block mb-1">Source API URL</label>
            <input
              type="url"
              value={sourceApi}
              onChange={(e) => setSourceApi(e.target.value)}
              placeholder="https://preview.example.com"
              className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
              data-testid="clone-source-api"
              required
            />
            {savedTargets.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] self-center mr-1">Saved:</span>
                {savedTargets.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSourceApi(t.api_url)}
                    className="kb-btn-outline !py-0.5 !px-2 !text-[10px] inline-flex items-center gap-1 group"
                    title={t.api_url}
                    data-testid={`clone-saved-${t.id}`}
                  >
                    {t.name}
                    <Trash2
                      size={9}
                      className="opacity-0 group-hover:opacity-70 hover:!opacity-100"
                      onClick={(e) => removeSavedTarget(t.id, e)}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Source slug + creds -------------------------------------- */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Source tenant slug</label>
              <input
                type="text" value={sourceSlug}
                onChange={(e) => setSourceSlug(e.target.value)}
                placeholder="multibrand"
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
                data-testid="clone-source-slug" required
              />
            </div>
            <div>
              <label className="overline block mb-1">Target slug <span className="text-[10px] normal-case">(optional)</span></label>
              <input
                type="text" value={targetSlug}
                onChange={(e) => setTargetSlug(e.target.value)}
                placeholder="defaults to source"
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
                data-testid="clone-target-slug"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Source super-admin phone</label>
              <input
                type="tel" value={sourcePhone}
                onChange={(e) => setSourcePhone(e.target.value)}
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
                data-testid="clone-source-phone" required
              />
            </div>
            <div>
              <label className="overline block mb-1">Source OTP <span className="text-[10px] normal-case">(request on source first)</span></label>
              <input
                type="text" value={sourceOtp}
                onChange={(e) => setSourceOtp(e.target.value)}
                placeholder="557725"
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
                data-testid="clone-source-otp" required
              />
            </div>
          </div>

          {/* Flags ---------------------------------------------------- */}
          <div className="space-y-1.5 pt-1">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)}
                     data-testid="clone-flag-full" />
              <span><strong>Include live data</strong> — users, orders, carts, reviews (otherwise catalog only)</span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)}
                     data-testid="clone-flag-wipe" />
              <span><strong>Wipe target tenant data first</strong> — delete existing docs before importing (admin user is preserved)</span>
            </label>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={includeAppConfig} onChange={(e) => setIncludeAppConfig(e.target.checked)}
                     data-testid="clone-flag-app-config" />
              <span>
                <strong>Include app config</strong> — logo, Firebase project, iOS/Android bundle IDs.
                <em className="block text-[10px] text-[hsl(var(--kb-muted))]">Turn OFF if the target ships its own native app with its own Firebase project.</em>
              </span>
            </label>
          </div>

          {/* Advanced — save deployment ------------------------------- */}
          <button
            type="button" onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs text-[hsl(var(--kb-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--kb-primary))]"
            data-testid="clone-toggle-advanced"
          >
            <ChevronDown size={11} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
            Advanced
          </button>
          {showAdvanced && (
            <div className="pl-4 border-l-2 border-[hsl(var(--kb-border))] space-y-2">
              <label className="overline block">Save this source for next time as</label>
              <input
                type="text" value={saveAs}
                onChange={(e) => setSaveAs(e.target.value)}
                placeholder="e.g. preview, prod, staging"
                className="w-full border border-[hsl(var(--kb-border))] px-3 py-2 text-sm font-mono"
                data-testid="clone-save-as"
              />
              <p className="text-[10px] text-[hsl(var(--kb-muted))]">
                Only the URL is saved — never your credentials.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-[hsl(var(--kb-border))]">
          <button type="button" onClick={onClose} disabled={busy}
                  className="kb-btn-outline !py-1.5 !px-3 !text-[11px]"
                  data-testid="clone-modal-cancel">
            Cancel
          </button>
          <button type="submit" disabled={busy}
                  className="kb-btn-primary !py-1.5 !px-3 !text-[11px] inline-flex items-center gap-1"
                  data-testid="clone-modal-confirm">
            {busy && <Loader2 size={11} className="animate-spin" />}
            {busy ? "Cloning…" : "Clone tenant"}
          </button>
        </div>
      </form>
    </div>
  );
}
